<script lang="ts" setup>
  import axios from 'axios'

  import { onBeforeUnmount, onMounted, ref } from 'vue'

  import { useAxios } from '#vue-shared-lib/composables/axios'

  import { getPathFromHash } from '#ourahub-intra/utilities'

  const apiUrl = import.meta.env.VITE_OUTO_API_URL ?? ''

  const isLocalhost = !apiUrl || apiUrl.startsWith('http://localhost')

  const PROBE_TIMEOUT_MS = 5000

  // Check Twingate connection every 2 minutes
  const CHECK_INTERVAL_MS = 2 * 60 * 1000

  const { axiosClient } = useAxios({
    url: apiUrl,
    agent: 'ourahub-intra',
    version: import.meta.env.VITE_APP_VERSION,
    timeout: PROBE_TIMEOUT_MS,
    retryCount: 0,
  })

  // The cause of a failed health-check, or `null` until the first definitive verdict.
  type ConnectionIssue = 'twingate' | 'local-network'

  const connectionIssue = ref<ConnectionIssue | null>(null)
  const isChecking = ref(false)

  const showInfo = ref(false)

  // The dialog auto-opens only on the very first check; later checks update the banner only.
  const isFirstCheck = ref(true)

  function isOnHomeRoute(): boolean {
    const service = getPathFromHash(window.location.hash).split('/')[1]

    return !service || service === 'home'
  }

  /** Chrome's Local Network Access permission state, or `null` where the browser can't report it. */
  async function getLocalNetworkPermission(): Promise<PermissionState | null> {
    try {
      // `local-network-access` is Chromium-only (Chrome 142+) and not yet in the TS `PermissionName` union.
      const name: string = 'local-network-access'

      const status = await navigator.permissions.query({ name: name as PermissionName })

      return status.state
    } catch {
      return null
    }
  }

  /** Probes the Twingate-gated API and classifies a failure, or `null` on a successful (2xx) health-check. */
  async function probeConnection(): Promise<ConnectionIssue | null> {
    try {
      await axiosClient.get('/-/health-check')

      return null
    } catch (error) {
      // An LNA block fails before any response, so receiving one (even a 4xx/5xx) rules it out.
      // Fall back to the default Twingate banner without querying the permission.
      if (axios.isAxiosError(error) && error.response) {
        return 'twingate'
      }

      // No response: Chrome's Local Network Access can block the request to the backend's local
      // address space, sometimes without showing the enable prompt. Surface that case separately.
      const permission = await getLocalNetworkPermission()

      return permission === 'denied' || permission === 'prompt' ? 'local-network' : 'twingate'
    }
  }

  async function checkConnection(): Promise<void> {
    if (isLocalhost || isChecking.value) {
      return
    }

    // If browser offline, don't show an error
    if (!navigator.onLine) {
      connectionIssue.value = null

      return
    }

    const wasFirstCheck = isFirstCheck.value

    isChecking.value = true

    try {
      connectionIssue.value = await probeConnection()

      // Auto-open the info dialog only on the first check and only on the home route (no app loaded),
      // so it never pops up over a loaded micro-frontend or stacks on an app's own Twingate dialog.
      if (wasFirstCheck && connectionIssue.value && isOnHomeRoute()) {
        showInfo.value = true
      }
    } finally {
      isChecking.value = false
      isFirstCheck.value = false
    }
  }

  // Re-check when the user returns to the tab, e.g. after connecting Twingate.
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void checkConnection()
    }
  }

  let intervalId: ReturnType<typeof setInterval> | undefined

  onMounted(() => {
    void checkConnection()

    document.addEventListener('visibilitychange', onVisibilityChange)

    intervalId = setInterval(() => void checkConnection(), CHECK_INTERVAL_MS)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange)

    clearInterval(intervalId)
  })
</script>

<template lang="html">
  <v-footer v-if="connectionIssue" app border color="error" class="text-white" :order="1" :height="48">
    <v-icon
      class="mx-3"
      :icon="connectionIssue === 'local-network' ? 'mdi-router-wireless-off' : 'mdi-lan-disconnect'"
    />

    <span v-if="connectionIssue === 'local-network'">
      <b>Local network access is blocked.</b>
      Chrome blocked OuraHub from reaching the backend — allow local network access and retry.
    </span>

    <span v-else>
      <b>Twingate is not connected.</b>
      OuraHub services require Twingate — please connect to Twingate and retry.
    </span>

    <v-spacer />

    <o-btn-secondary class="mr-2" @click="showInfo = true">More info</o-btn-secondary>

    <o-btn-primary color="white" :loading="isChecking" @click="checkConnection()">Retry</o-btn-primary>

    <TwingateInfo v-model="showInfo" />
  </v-footer>
</template>
